import * as Sentry from '@sentry/react';

interface ExtendedNavigator extends Navigator {
  deviceMemory?: number;
  connection?: {
    effectiveType?: string;
  };
}

// Helper to parse environment variables
const parseEnvBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
  if (value === undefined || value === '') return defaultValue;
  return value === 'true';
};

const parseEnvNumber = (value: string | undefined, defaultValue: number): number => {
  if (value === undefined || value === '') return defaultValue;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

// Environment variable configuration (must be set per environment {local, development, production})
const config = {
  tracesSampleRate: parseEnvNumber(process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE, 0),
  profilesSampleRate: parseEnvNumber(process.env.NEXT_PUBLIC_SENTRY_PROFILES_SAMPLE_RATE, 0),
  replaysSessionSampleRate: parseEnvNumber(process.env.NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0),
  enableProfiling: parseEnvBoolean(process.env.NEXT_PUBLIC_SENTRY_ENABLE_PROFILING, false),
  enableReplay: parseEnvBoolean(process.env.NEXT_PUBLIC_SENTRY_ENABLE_REPLAY, false),
  captureConsole: parseEnvBoolean(process.env.NEXT_PUBLIC_SENTRY_CAPTURE_CONSOLE, false),
  captureHttp: parseEnvBoolean(process.env.NEXT_PUBLIC_SENTRY_CAPTURE_HTTP, false),
  sendPii: parseEnvBoolean(process.env.NEXT_PUBLIC_SENTRY_SEND_PII, false)
};

const getDeviceContext = () => {
  const nav = navigator as ExtendedNavigator;
  return {
    deviceMemory: nav.deviceMemory ? `${nav.deviceMemory} GB` : undefined,
    hardwareConcurrency: nav.hardwareConcurrency || undefined,
    effectiveConnectionType: nav.connection?.effectiveType || undefined,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    devicePixelRatio: window.devicePixelRatio || 1,
    touchSupport: 'ontouchstart' in globalThis || nav.maxTouchPoints > 0,
    language: navigator.language,
    online: navigator.onLine
  };
};

// The old clip link signed in its query; the restream link signs in its path: /v1/<job>/<expires>/<chunk>/<frames>/<sig>/<file>.
const withoutQuery = (url: string): string => {
  const query = url.indexOf('?');
  return query === -1 ? url : url.slice(0, query);
};

const isResource = (op: string | undefined): boolean => op?.startsWith('resource.') === true;

const PATH_SIGNATURE = /(\/v1\/\d+\/\d+\/\d+\/\d+\/)[\w-]{43}(?=\/)/g;

// The Kitchen Floor feed sends its access token as the `bearer.<jwt>` WebSocket subprotocol;
// a browser quotes subprotocols in some error messages.
const BEARER_TOKEN = /bearer\.[\w.-]+/gi;

const redactString = (value: string): string =>
  value.replaceAll(PATH_SIGNATURE, '$1[Filtered]').replaceAll(BEARER_TOKEN, 'bearer.[Filtered]');

const redactValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return redactString(value);
  }
  if (Array.isArray(value)) {
    return value.map(redactValue);
  }
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, redactValue(entry)]));
  }
  return value;
};

const redact = <T>(value: T): T => redactValue(value) as T;

// Build integrations array based on configuration
const buildIntegrations = () => {
  const integrations: ReturnType<typeof Sentry.browserTracingIntegration>[] = [
    Sentry.browserTracingIntegration({ enableInp: true })
  ];

  // Conditionally add profiling (requires Document-Policy: js-profiling header)
  if (config.enableProfiling) {
    integrations.push(Sentry.browserProfilingIntegration() as ReturnType<typeof Sentry.browserTracingIntegration>);
  }

  // Conditionally add console logging
  if (config.captureConsole) {
    integrations.push(
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }) as ReturnType<typeof Sentry.browserTracingIntegration>
    );
  }

  // Conditionally add HTTP client integration
  if (config.captureHttp) {
    integrations.push(Sentry.httpClientIntegration() as ReturnType<typeof Sentry.browserTracingIntegration>);
  }

  return integrations;
};

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_KEY,
  debug: process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production',
  sendDefaultPii: config.sendPii,

  integrations: buildIntegrations(),

  // Profiling (requires Document-Policy header)
  profilesSampleRate: config.enableProfiling ? config.profilesSampleRate : 0,
  profileSessionSampleRate: config.enableProfiling ? config.profilesSampleRate : 0,

  // Session Replay (lazy-loaded based on config)
  replaysSessionSampleRate: config.enableReplay ? config.replaysSessionSampleRate : 0,
  replaysOnErrorSampleRate: config.enableReplay ? 1.0 : 0,

  enableLogs: config.captureConsole,
  release: process.env.NEXT_PUBLIC_VERSION || 'calo-kds-app@1.0.0',
  tracesSampleRate: config.tracesSampleRate,
  tracePropagationTargets: [/^\//, /^https:\/\/.*\.calo\.app\/api/],
  enabled: !!process.env.NEXT_PUBLIC_SENTRY_KEY && process.env.NEXT_PUBLIC_SENTRY_ENABLED !== 'false',
  environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || process.env.NEXT_PUBLIC_ENVIRONMENT || 'local',
  normalizeDepth: 10,

  initialScope: (scope) => {
    // Custom device tags that Sentry doesn't collect automatically
    const deviceInfo = getDeviceContext();
    if (deviceInfo.deviceMemory) scope.setTag('deviceMemory', deviceInfo.deviceMemory);
    if (deviceInfo.hardwareConcurrency) scope.setTag('hardwareConcurrency', String(deviceInfo.hardwareConcurrency));
    if (deviceInfo.effectiveConnectionType) scope.setTag('effectiveConnectionType', deviceInfo.effectiveConnectionType);
    scope.setTag('screenResolution', deviceInfo.screenResolution);
    scope.setTag('touchSupport', String(deviceInfo.touchSupport));

    return scope;
  },

  beforeSend(event) {
    // Captures dynamic values (viewportSize, online status etc) at the moment of error
    event.extra ??= {};
    event.extra.deviceInfo = getDeviceContext();

    return redact(event);
  },

  beforeSendTransaction(event) {
    return redact(event);
  },

  beforeSendLog(log) {
    // `message` can be a String object, which the recursive copy would split into its characters.
    return redact({ ...log, message: String(log.message) });
  },

  // Xhr and fetch breadcrumbs carry the URL; they are not normalised yet, so only the two strings are rewritten.
  beforeBreadcrumb(breadcrumb) {
    const url: unknown = breadcrumb.data?.url;
    return {
      ...breadcrumb,
      ...(breadcrumb.message === undefined ? {} : { message: redactString(breadcrumb.message) }),
      ...(typeof url === 'string' ? { data: { ...breadcrumb.data, url: redactString(url) } } : {})
    };
  },

  beforeSendSpan(span) {
    if (!isResource(span.op)) {
      return redact(span);
    }

    const fullUrl = span.data['url.full'];
    return redact({
      ...span,
      description: span.description === undefined ? undefined : withoutQuery(span.description),
      data: typeof fullUrl === 'string' ? { ...span.data, 'url.full': withoutQuery(fullUrl) } : span.data
    });
  }
});

// Lazy-load Session Replay based on configuration
const lazyLoadReplay = () => {
  if (!config.enableReplay) return;

  void import('@sentry/react').then((lazyLoadedSentry) => {
    lazyLoadedSentry.addIntegration(
      lazyLoadedSentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
        beforeAddRecordingEvent(event) {
          if (event.data.tag !== 'performanceSpan') {
            return event;
          }

          const { payload } = event.data;
          const description = isResource(payload.op) ? withoutQuery(payload.description) : payload.description;
          return { ...event, data: { ...event.data, payload: redact({ ...payload, description }) } };
        }
      })
    );
  });
};

if (document.readyState === 'complete') {
  lazyLoadReplay();
} else {
  window.addEventListener('load', lazyLoadReplay);
}
